import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, Check, X } from "lucide-react";
import MerchantAuthLayout from "./MerchantAuthLayout";
import { useMerchantAuth } from "../../context/MerchantAuthContext";

const RULES = [
  { key: "len", label: "At least 12 characters", test: (p) => p.length >= 12 },
  { key: "num", label: "Contains a number", test: (p) => /\d/.test(p) },
  { key: "sym", label: "Contains a symbol", test: (p) => /[!@#$%^&*()_+\-={}\[\]:;"'<>,.?/~`|\\]/.test(p) },
  { key: "nospace", label: "No spaces", test: (p) => p.length > 0 && !/\s/.test(p) },
];

export default function Signup() {
  const { user, loading, signup } = useMerchantAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Brief 3.4 §2.7 — magic-link prefill. The /onboard/:token landing page
  // resolves the token via /api/onboarding/prefill and forwards us here
  // with these query params populated. The token itself is opaque to this
  // form — we just attach it to the signup payload so the server can
  // atomically consume it.
  const prefillEmail = searchParams.get("email") || "";
  const prefillLegalName = searchParams.get("legal_name") || "";
  const onboardingToken = searchParams.get("onboarding_token") || "";
  const isMagicLink = !!onboardingToken;

  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && user) navigate("/app", { replace: true });
  }, [user, loading, navigate]);

  const allPass = RULES.every((r) => r.test(password));
  const canSubmit = !!email.trim() && allPass && !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setSubmitting(true);
    try {
      const data = await signup(email.trim().toLowerCase(), password, onboardingToken || undefined);
      if (data?.user && data?.token) {
        navigate("/app", { replace: true });
      } else {
        // Server returned the "pending" response — either a brand-new
        // account that needs email verification (future), or a duplicate
        // signup attempt that the server has handled out-of-band by
        // emailing the existing account holder. Either way: send the
        // merchant to login with a friendly banner.
        navigate("/login?reason=check-email", { replace: true });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Detail can now be a {reason, message} object (new shape) or a
      // plain string (older paths). Normalise both ways before rendering.
      const human =
        detail && typeof detail === "object"
          ? detail.message
          : typeof detail === "string"
          ? detail
          : null;
      // Magic-link errors get a friendlier copy than the generic "could
      // not create your account" — the merchant didn't do anything wrong;
      // the link is just stale.
      if (isMagicLink && err?.response?.status === 410) {
        setError(human || "This onboarding link is no longer valid. Please ask your Neptune contact for a new one.");
      } else if (isMagicLink && err?.response?.status === 404) {
        setError("This onboarding link wasn't recognised. Please ask your Neptune contact for a new one.");
      } else {
        setError(human || "Could not create your account. Please try again.");
      }
      setSubmitting(false);
    }
  };

  return (
    <MerchantAuthLayout
      eyebrow={isMagicLink ? "Welcome to Neptune" : "Create account"}
      title={isMagicLink && prefillLegalName ? `Set up ${prefillLegalName}'s account` : "Get started with Neptune"}
      subtitle={
        isMagicLink
          ? "Choose a password to access your dashboard. We've already pre-filled what we have."
          : "Email and a strong password — that's all we need. Your back office opens instantly."
      }
      testId="merchant-signup-page"
    >
      <form onSubmit={onSubmit} className="space-y-5" data-testid="merchant-signup-form">
        {isMagicLink && (
          <div
            data-testid="signup-magic-link-banner"
            className="rounded-xl border border-[#00E5FF]/20 bg-[#00E5FF]/[0.04] text-cyan-100 text-[12.5px] px-4 py-3 leading-relaxed"
          >
            You're signing up via your Neptune onboarding link. We've pre-filled your work email — just choose a password.
          </div>
        )}
        <Field
          label="Work email"
          type="email"
          value={email}
          onChange={setEmail}
          placeholder="you@company.com"
          autoComplete="email"
          testId="signup-email-input"
          required
          readOnly={isMagicLink && !!prefillEmail}
        />

        <div>
          <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
            Password
          </label>
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Choose a strong password"
              autoComplete="new-password"
              data-testid="signup-password-input"
              required
              className="w-full rounded-xl bg-white/[0.03] border border-white/[0.08] focus:border-[#00E5FF]/50 focus:bg-white/[0.05] focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/20 transition-all px-4 py-3 pr-11 text-[14px] text-white placeholder:text-zinc-600"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              data-testid="signup-toggle-password"
              aria-label={showPw ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
            >
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          <ul className="mt-3 grid grid-cols-2 gap-1.5" data-testid="signup-password-checklist">
            {RULES.map((r) => {
              const ok = r.test(password);
              return (
                <li
                  key={r.key}
                  data-testid={`pw-rule-${r.key}-${ok ? "pass" : "fail"}`}
                  className={`flex items-center gap-1.5 text-[11.5px] transition-colors ${
                    ok ? "text-emerald-300" : "text-zinc-500"
                  }`}
                >
                  {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                  {r.label}
                </li>
              );
            })}
          </ul>
        </div>

        {error && (
          <div
            data-testid="signup-error"
            className="rounded-xl border border-rose-400/20 bg-rose-400/[0.05] text-rose-200 text-[12.5px] px-4 py-3"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          data-testid="signup-submit-btn"
          className="group inline-flex items-center justify-center gap-2 rounded-full bg-white text-black hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all w-full px-5 py-3.5 text-[14px] font-medium"
        >
          {submitting ? "Creating account…" : "Create account"}
          {!submitting && <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />}
        </button>

        <p className="text-[11.5px] text-zinc-500 text-center leading-relaxed">
          By continuing you agree to our{" "}
          <Link to="/legal/terms" className="text-zinc-300 hover:text-white underline-offset-2 hover:underline">
            Terms
          </Link>{" "}
          and{" "}
          <Link to="/legal/privacy" className="text-zinc-300 hover:text-white underline-offset-2 hover:underline">
            Privacy Policy
          </Link>
          .
        </p>
      </form>

      <div className="mt-7 pt-6 border-t border-white/[0.06] text-center text-[13px] text-zinc-400">
        Already have an account?{" "}
        <Link
          to="/login"
          data-testid="signup-go-to-login"
          className="text-white hover:text-[#00E5FF] transition-colors font-medium"
        >
          Log in
        </Link>
      </div>
    </MerchantAuthLayout>
  );
}

function Field({ label, type, value, onChange, placeholder, autoComplete, testId, required, readOnly }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        data-testid={testId}
        required={required}
        readOnly={!!readOnly}
        className={`w-full rounded-xl bg-white/[0.03] border border-white/[0.08] focus:border-[#00E5FF]/50 focus:bg-white/[0.05] focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/20 transition-all px-4 py-3 text-[14px] text-white placeholder:text-zinc-600 ${readOnly ? "opacity-80 cursor-not-allowed" : ""}`}
      />
    </div>
  );
}
